<template>
    <div class="catalog-entry-cell">
        <div
            v-if="showCatalogEntries && catalogEntries.length"
            class="catalog-entry-cell__entries"
        >
            <v-chip
                v-for="entry in catalogEntries"
                :key="entry.id"
                x-small
                label
                outlined
                class="catalog-entry-cell__chip"
            >
                <v-icon x-small left>
                    {{ entry.is_default ? "mdi-star-outline" : "mdi-tag-outline" }}
                </v-icon>
                {{ formatEntryLabel(entry) }}
            </v-chip>
        </div>
        <span
            v-else-if="showCatalogEntries && !showFormatPicker"
            class="text-caption grey--text text--darken-1"
        >
            {{ $translate("notInCatalog") }}
        </span>

        <FormatSelect
            v-if="showFormatPicker"
            :value="formatId"
            :formats="compatibleFormatOptions"
            :label="$translate('format')"
            :unit-option-label="unitOptionLabel"
            :loading="formatsLoading"
            :error-messages="formatErrors"
            variant="sale"
            input-class="catalog-entry-cell__format mt-1"
            @input="onFormatChange"
        />
    </div>
</template>

<script>
import { FORMAT_SELECT_UNIT } from "@/constants/format-select";
import GlobalMixins from "@/mixins/global";

export default {
    name: "AddToCatalogCatalogCell",
    mixins: [GlobalMixins],
    props: {
        product: {
            type: Object,
            default: null,
        },
        catalogEntries: {
            type: Array,
            default: () => [],
        },
        showCatalogEntries: {
            type: Boolean,
            default: true,
        },
        showFormatPicker: {
            type: Boolean,
            default: false,
        },
        formatId: {
            type: [Number, String],
            default: null,
        },
        formatOptionsList: {
            type: Array,
            default: () => [],
        },
        formatsLoading: {
            type: Boolean,
            default: false,
        },
        formatErrors: {
            type: Array,
            default: () => [],
        },
        productFormatName: {
            type: String,
            default: null,
        },
    },
    computed: {
        unitOptionLabel() {
            return this.productFormatName || this.$translate("defaultEntry");
        },
        compatibleFormatOptions() {
            const formats = this.formatOptionsList || [];
            if (!this.product) {
                return formats;
            }
            return formats.filter((format) =>
                this.$utils.formatCompatibleWithProduct(this.product, format),
            );
        },
    },
    watch: {
        compatibleFormatOptions: {
            immediate: true,
            handler(formats) {
                this.clearIncompatibleFormat(formats);
            },
        },
    },
    methods: {
        clearIncompatibleFormat(formats) {
            if (!this.showFormatPicker || this.formatsLoading || !this.product) {
                return;
            }
            if (!(this.formatOptionsList || []).length) {
                return;
            }
            if (
                this.formatId == null
                || this.formatId === ""
                || this.formatId === FORMAT_SELECT_UNIT
            ) {
                return;
            }
            const stillValid = (formats || []).some(
                (format) => format.id == this.formatId,
            );
            if (!stillValid) {
                this.$emit("format-change", null);
            }
        },
        onFormatChange(formatId) {
            this.$emit("format-change", formatId);
        },
        formatEntryLabel(entry) {
            if (entry.format_name) {
                return entry.format_name;
            }
            if (entry.sale_format_name) {
                return entry.sale_format_name;
            }
            if (!entry.format && this.productFormatName) {
                return this.productFormatName;
            }
            if (entry.sale_content_display?.quantity != null) {
                const quantity = this.$utils.formatDecimalInputValue(
                    entry.sale_content_display.quantity,
                    3,
                );
                return `${quantity} ${entry.sale_content_display.unit}`;
            }
            if (entry.is_default) {
                return this.productFormatName || this.$translate("defaultEntry");
            }
            return entry.label;
        },
    },
    i18n: {
        messages: {
            en: {
                defaultEntry: "Per unit",
                format: "Format to add",
                notInCatalog: "Not in catalog",
            },
            fr: {
                defaultEntry: "À l'unité",
                format: "Format à ajouter",
                notInCatalog: "Pas au catalogue",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.catalog-entry-cell {
    min-width: 230px;
}

.catalog-entry-cell__entries {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    margin: 8px 0;
}

.catalog-entry-cell__chip {
    max-width: 100%;
    background-color: #fff !important;
}

.catalog-entry-cell__format {
    max-width: 340px;
}
</style>
